import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, PanResponder, Platform, Pressable, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

import { insertIntoArray } from '../../../common/utils/utils';
import { TextInput } from '../../components';
import Text from '../../components/text/Text';
import { ActionDock, Card, EmptyState, Screen } from '../../design/components';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { useDriverDay } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import { getStopPositionName, getStopSiteName } from './stopDetails';
import useDriverDayActions from './useDriverDayActions';

const ROW_GAP = 8;

const formatNumberList = (numbers) => {
	if (numbers.length === 1) {
		return `Stop ${numbers[0]} is`;
	}

	const isRunFromOne = numbers.every((number, index) => number === index + 1);
	if (isRunFromOne) {
		return `Stops 1 to ${numbers.length} are`;
	}

	return `Stops ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]} are`;
};

// Keeps the driver's order for stops still movable and appends any newly movable ones.
const reconcileOrder = (currentIds, availableIds) => {
	const available = new Set(availableIds);
	const kept = currentIds.filter(id => available.has(id));
	const keptSet = new Set(kept);

	return [...kept, ...availableIds.filter(id => !keptSet.has(id))];
};

const ArrowButton = ({ icon, label, onPress, disabled }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();

	return (
		<Pressable
			onPress={onPress}
			disabled={disabled}
			accessibilityRole="button"
			accessibilityLabel={label}
			accessibilityState={{ disabled }}
			style={({ pressed }) => [styles.arrow, disabled && styles.arrowDisabled, pressed && styles.pressed]}
		>
			<Feather name={icon} size={20} color={disabled ? colors.disabledText : colors.accent} />
		</Pressable>
	);
};

const ReorderRow = memo(({
	docket,
	number,
	index,
	count,
	isDragging,
	dragOffset,
	onMove,
	onDragStart,
	onDragMove,
	onDragEnd,
	onLayout,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const handlersRef = useRef({ onDragStart, onDragMove, onDragEnd });
	handlersRef.current = { onDragStart, onDragMove, onDragEnd };
	const panResponder = useMemo(() => PanResponder.create({
		onStartShouldSetPanResponder: () => true,
		onMoveShouldSetPanResponderCapture: () => true,
		// The list must not scroll away while a stop is being dragged.
		onPanResponderTerminationRequest: () => false,
		onPanResponderGrant: () => handlersRef.current.onDragStart(docket.id),
		onPanResponderMove: (_, gesture) => handlersRef.current.onDragMove(gesture.dy),
		onPanResponderRelease: () => handlersRef.current.onDragEnd(),
		onPanResponderTerminate: () => handlersRef.current.onDragEnd(),
	}), [docket.id]);
	// A native recognizer keeps the handle's drag from being cancelled by parent gestures.
	const nativeGesture = useMemo(() => Gesture.Pan()
		.minDistance(0)
		.runOnJS(true)
		.onStart(() => handlersRef.current.onDragStart(docket.id))
		.onUpdate(event => handlersRef.current.onDragMove(event.translationY))
		.onFinalize(() => handlersRef.current.onDragEnd()), [docket.id]);
	const name = getStopPositionName(docket);
	const siteName = getStopSiteName(docket);
	const machineNumber = docket.machine?.assetId;
	const handle = (
		<View
			{...(Platform.OS === 'web' ? panResponder.panHandlers : {})}
			style={styles.handle}
			accessible={true}
			accessibilityLabel={`Drag ${name} to a new position`}
		>
			<Feather name="menu" size={20} color={colors.iconMuted} />
		</View>
	);

	return (
		<Animated.View
			onLayout={event => onLayout(docket.id, event.nativeEvent.layout.height)}
			style={[
				styles.row,
				isDragging && styles.rowDragging,
				isDragging && { transform: [{ translateY: dragOffset }] },
			]}
		>
			{Platform.OS === 'web' ? handle : <GestureDetector gesture={nativeGesture}>{handle}</GestureDetector>}
			<View style={styles.number}>
				<Text style={styles.numberText}>{number}</Text>
			</View>
			<View style={styles.rowBody}>
				<Text style={styles.rowTitle}>{name}</Text>
				{/* Separate texts so the machine number wraps as a whole, never mid-number. */}
				{Boolean(siteName || machineNumber) && (
					<View style={styles.rowMetaLine}>
						{Boolean(siteName) && <Text style={styles.rowMeta}>{machineNumber ? `${siteName} · ` : siteName}</Text>}
						{Boolean(machineNumber) && <Text style={styles.rowMeta}>{machineNumber}</Text>}
					</View>
				)}
			</View>
			<ArrowButton icon="chevron-up" label={`Move ${name} up`} onPress={() => onMove(docket.id, index - 1)} disabled={index === 0} />
			<ArrowButton icon="chevron-down" label={`Move ${name} down`} onPress={() => onMove(docket.id, index + 1)} disabled={index === count - 1} />
		</Animated.View>
	);
});

/**
 * Reorder stops: the stops not done and not started, with drag handles and
 * up/down arrows (SPEC decision 16). Done and in-progress stops keep their
 * places; the reason stays required as in the classic reorder dialog. Saves
 * through useDriverDayActions().submitNextStops (exact order).
 */
const ReorderStopsView = () => {
	const navigation = useNavigation();
	const backLabel = useBackLabel();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const day = useDriverDay();
	const { submitNextStops, isMakingNextStop, requireTrainingConnection } = useDriverDayActions(day);
	const { sortedDockets, reorderableStops, isDocketDone, trainingOffline } = day;
	const reorderableIds = useMemo(() => reorderableStops.map(stop => stop.id), [reorderableStops]);
	const [orderedIds, setOrderedIds] = useState(reorderableIds);
	const [reason, setReason] = useState('');
	const [draggingId, setDraggingId] = useState(null);
	const dragOffset = useRef(new Animated.Value(0)).current;
	const orderRef = useRef(orderedIds);
	orderRef.current = orderedIds;
	const rowHeightsRef = useRef({});
	const dragRef = useRef({ id: null, shifted: 0 });

	// A stop that starts or finishes while reordering leaves the list.
	useEffect(() => {
		setOrderedIds(currentIds => reconcileOrder(currentIds, reorderableIds));
	}, [reorderableIds]);

	const docketsById = useMemo(() => new Map(sortedDockets.map(docket => [docket.id, docket])), [sortedDockets]);
	const reorderableSet = useMemo(() => new Set(reorderableIds), [reorderableIds]);
	// Movable stops fill the positions they already hold; fixed stops keep theirs.
	const slotNumbers = useMemo(() => sortedDockets
		.map((docket, index) => (reorderableSet.has(docket.id) ? index + 1 : null))
		.filter(Boolean), [reorderableSet, sortedDockets]);
	const fixedDockets = useMemo(() => sortedDockets.filter(docket => !reorderableSet.has(docket.id)), [reorderableSet, sortedDockets]);
	const fixedNumbers = useMemo(() => sortedDockets
		.map((docket, index) => (reorderableSet.has(docket.id) ? null : index + 1))
		.filter(Boolean), [reorderableSet, sortedDockets]);
	const fixedDoneCount = fixedDockets.filter(isDocketDone).length;
	const fixedInProgressCount = fixedDockets.length - fixedDoneCount;
	const hasOrderChanged = orderedIds.length === reorderableIds.length &&
		orderedIds.some((id, index) => id !== reorderableIds[index]);
	const canSave = hasOrderChanged && Boolean(reason.trim()) && !trainingOffline;

	const moveStop = useCallback((stopId, destinationIndex) => {
		setOrderedIds((currentIds) => {
			const sourceIndex = currentIds.indexOf(stopId);
			if (sourceIndex < 0 || destinationIndex < 0 || destinationIndex >= currentIds.length || sourceIndex === destinationIndex) {
				return currentIds;
			}

			return insertIntoArray(currentIds.filter(id => id !== stopId), destinationIndex, stopId);
		});
	}, []);

	const onRowLayout = useCallback((id, height) => {
		rowHeightsRef.current[id] = height;
	}, []);
	const onDragStart = useCallback((id) => {
		dragRef.current = { id, shifted: 0 };
		dragOffset.setValue(0);
		setDraggingId(id);
	}, [dragOffset]);
	// Swap with a neighbour once the row passes half of it; keep the finger offset.
	const onDragMove = useCallback((dy) => {
		const { id } = dragRef.current;
		if (!id) {
			return;
		}

		const ids = orderRef.current;
		const index = ids.indexOf(id);
		let translate = dy - dragRef.current.shifted;
		const nextId = ids[index + 1];
		const previousId = ids[index - 1];
		const nextStep = nextId ? (rowHeightsRef.current[nextId] || 0) + ROW_GAP : 0;
		const previousStep = previousId ? (rowHeightsRef.current[previousId] || 0) + ROW_GAP : 0;
		if (nextId && translate > nextStep / 2) {
			dragRef.current.shifted += nextStep;
			translate -= nextStep;
			const nextIds = insertIntoArray(ids.filter(item => item !== id), index + 1, id);
			orderRef.current = nextIds;
			setOrderedIds(nextIds);
		}
		else if (previousId && translate < -previousStep / 2) {
			dragRef.current.shifted -= previousStep;
			translate += previousStep;
			const nextIds = insertIntoArray(ids.filter(item => item !== id), index - 1, id);
			orderRef.current = nextIds;
			setOrderedIds(nextIds);
		}
		dragOffset.setValue(translate);
	}, [dragOffset]);
	const onDragEnd = useCallback(() => {
		dragRef.current = { id: null, shifted: 0 };
		Animated.timing(dragOffset, { toValue: 0, duration: 120, useNativeDriver: false }).start(() => setDraggingId(null));
	}, [dragOffset]);

	const save = useCallback(async () => {
		if (!canSave || !requireTrainingConnection()) {
			return;
		}

		if (await submitNextStops(orderedIds, reason.trim())) {
			navigation.goBack();
		}
	}, [canSave, navigation, orderedIds, reason, requireTrainingConnection, submitNextStops]);

	const cancelButton = (
		<Pressable
			onPress={() => navigation.goBack()}
			accessibilityRole="button"
			style={({ pressed }) => [styles.cancel, pressed && styles.pressed]}
		>
			<Text style={styles.cancelText}>Cancel</Text>
		</Pressable>
	);

	const getDockHint = () => {
		if (!hasOrderChanged) {
			return 'Move a stop to change the order';
		}

		return reason.trim() ? undefined : 'Add a reason to save the new order';
	};

	return (
		<Screen
			backLabel={backLabel}
			headerRight={cancelButton}
			heading="Reorder stops"
			subheading="Drag or use the arrows. Done and in-progress stops stay where they are."
			scrollEnabled={!draggingId}
			banner={<DriverTrainingBanner />}
			dock={orderedIds.length > 1 ? (
				<ActionDock
					hint={getDockHint()}
					action={{ label: 'Save stop order', onPress: save, loading: isMakingNextStop, disabled: !canSave || isMakingNextStop }}
				/>
			) : undefined}
		>
			{fixedDockets.length > 0 && orderedIds.length > 0 && (
				<Card tone="raised" style={styles.fixedCard}>
					<Feather name="lock" size={18} color={colors.iconMuted} />
					<Text style={styles.fixedText}>
						{`${formatNumberList(fixedNumbers)} fixed (${[
							fixedDoneCount ? `${fixedDoneCount} done` : null,
							fixedInProgressCount ? `${fixedInProgressCount} in progress` : null,
						].filter(Boolean).join(', ')})`}
					</Text>
				</Card>
			)}
			{orderedIds.length > 1
				? (
					<>
						<View style={styles.list}>
							{orderedIds.map((id, index) => {
								const docket = docketsById.get(id);
								if (!docket) {
									return null;
								}

								return (
									<ReorderRow
										key={id}
										docket={docket}
										number={slotNumbers[index]}
										index={index}
										count={orderedIds.length}
										isDragging={draggingId === id}
										dragOffset={dragOffset}
										onMove={moveStop}
										onDragStart={onDragStart}
										onDragMove={onDragMove}
										onDragEnd={onDragEnd}
										onLayout={onRowLayout}
									/>
								);
							})}
						</View>
						<View style={styles.reason}>
							<Text style={styles.reasonLabel}>Reason for the change</Text>
							<TextInput
								value={reason}
								onChange={setReason}
								placeholder="e.g. opening hours, fresh food needs earlier delivery"
								multiline={true}
								maxLength={300}
								style={styles.reasonInput}
								accessibilityLabel="Reason for the change"
							/>
						</View>
					</>
				)
				: <EmptyState title="Nothing to reorder" message="Fewer than two stops are left that have not been started." />}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	cancel: {
		minHeight: touch.min,
		justifyContent: 'center',
		paddingHorizontal: space.sm,
	},
	cancelText: {
		...type.rowTitle,
		color: colors.accent,
	},
	pressed: {
		opacity: 0.7,
	},
	fixedCard: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		paddingVertical: space.md,
	},
	fixedText: {
		...type.secondary,
		color: colors.textSecondary,
		flex: 1,
	},
	list: {
		gap: ROW_GAP,
	},
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		minHeight: touch.rowWithSubtitle,
		paddingVertical: space.sm,
		paddingRight: space.sm,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	rowDragging: {
		zIndex: 1,
		borderColor: colors.currentBorder,
		backgroundColor: colors.current,
	},
	handle: {
		width: touch.min,
		alignSelf: 'stretch',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'grab',
		userSelect: 'none',
		// Phone browsers otherwise take a drag on the handle as a page scroll.
		touchAction: 'none',
	},
	number: {
		minWidth: 28,
		height: 28,
		borderRadius: radius.pill,
		paddingHorizontal: space.xs,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: colors.chip,
	},
	numberText: {
		...type.secondarySemibold,
		color: colors.text,
	},
	rowBody: {
		flex: 1,
		gap: 2,
	},
	rowTitle: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	rowMetaLine: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	rowMeta: {
		...type.caption,
		color: colors.textSecondary,
		flexShrink: 1,
	},
	arrow: {
		width: touch.min,
		height: touch.min,
		borderRadius: radius.control,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		alignItems: 'center',
		justifyContent: 'center',
	},
	arrowDisabled: {
		borderColor: colors.border,
	},
	reason: {
		gap: space.sm,
	},
	reasonLabel: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	reasonInput: {
		minHeight: 88,
		textAlignVertical: 'top',
	},
});

export default ReorderStopsView;
